import axios from 'axios'
import { createAction, createReducer } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import { apiCallBegan } from './apiCalls'
import { exchangePlaylearningAdminToken } from '../api/vccLeaderboard'
import API from '../constants/api'
import ROUTES, { getLobbyUrl } from '../constants/routes'
import { USER_TYPES } from '../constants/users'
import i18n from '../i18n'
import tokenRefresh from './middleware/token-refresh'
import { cleanUserTeam, loadUserTeam } from './user/team'
import { cleanUserGame, loadUserGame } from './user/game'
import { cleanUserBank } from './user/bank'

// ACTIONS
const loggedIn = createAction('auth/loggedIn')
const loggedOut = createAction('auth/loggedOut')
const signedUp = createAction('auth/signedUp')

const logInError = createAction('auth/loginError')
const signUpError = createAction('auth/signedUpError')

const gameCreated = createAction('auth/gameCreated')
const gameCreateError = createAction('auth/gameCreateError')
const lastCreatedGameCleared = createAction('auth/lastCreatedGameCleared')
const lastCreatedGameMetadataCleared = createAction('auth/lastCreatedGameMetadataCleared')

const gameJoined = createAction('auth/gameJoined')
const gameJoinError = createAction('auth/gameJoinError')

const gameStarted = createAction('auth/gameStarted')
const gameStartError = createAction('auth/gameStartError')

export const gameInfoFetched = createAction('auth/gameInfoFetched')
const lobbyPublicDataCleared = createAction('auth/lobbyPublicDataCleared')
const lobbyTeamsUpdated = createAction('auth/lobbyTeamsUpdated')
const teamNameUpdated = createAction('auth/teamNameUpdated')

// Restore lastCreatedGame from localStorage if available
const getStoredLastCreatedGame = () => {
  try {
    const stored = localStorage.getItem('smb_last_created_game')
    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
}

// Restore userType when we have a token (e.g. after refresh) so login redirect works
const getStoredUserType = () => {
  const token = localStorage.getItem('smb_access_token')
  if (!token) return null
  return localStorage.getItem('smb_user_type')
}

const wait = (delay) =>
  new Promise((resolve) => {
    setTimeout(resolve, delay)
  })

/** Email used for admin lobby reconciliation (player seat or logged-in user / facilitator). */
const resolveLobbySyncEmail = (state) => {
  const fromPlayer = state.auth.gameInfo?.current_player?.email
  if (fromPlayer) return fromPlayer
  return state.user?.info?.data?.email ?? null
}

async function syncAdminLobbyAfterSeatChange(getState) {
  const state = getState()
  const adminGameId = state.auth.gameInfo?.admin_game_id ?? state.auth.lastCreatedGame?.game_id
  const email = resolveLobbySyncEmail(state)
  if (!adminGameId || !email) return
  try {
    await axios.post(
      `${API.ADMIN_API_URL}/api/v1/auth/games/sync_lobby_from_api`,
      { game_id: adminGameId, email },
      { params: API.DEFAULT_PARAMS }
    )
  } catch {
    // Admin sync failure is non-blocking; API state is authoritative
  }
}

const surrenderStateSynced = (state) => {
  const teamState = state.user?.team?.data?.general_state
  const gameState = state.user?.game?.data?.aasm_state

  return teamState === 'game_over' || teamState === 'winner' || gameState === 'finished'
}

const syncSurrenderState = async (dispatch, getState, attemptsLeft = 5) => {
  await Promise.all([dispatch(loadUserTeam()), dispatch(loadUserGame())])

  if (surrenderStateSynced(getState()) || attemptsLeft <= 1) return

  await wait(300)
  await syncSurrenderState(dispatch, getState, attemptsLeft - 1)
}

const initialState = {
  loggedIn: localStorage.getItem('smb_access_token'),
  userType: getStoredUserType(),
  lastCreatedGame: getStoredLastCreatedGame(),
  gameInfo: null,
  lobbyTeams: null // Teams from WebSocket; used so lobby updates in real time even before gameInfo loads
}

// REDUCER
const authReducer = createReducer(initialState, {
  [loggedIn.type]: (state, action) => {
    state.loggedIn = true
    const userType = action.payload.user_type
    state.userType = userType
    localStorage.setItem('smb_access_token', action.payload.access_token)
    localStorage.setItem('smb_refresh_token', action.payload.refresh_token)
    if (userType) localStorage.setItem('smb_user_type', userType)
    toast.success(i18n.t('login.success'), { autoClose: 3000 })
  },
  [loggedOut.type]: (state) => {
    state.loggedIn = false
    state.userType = null
    state.lastCreatedGame = null
    state.gameInfo = null
    state.lobbyTeams = null
    localStorage.removeItem('smb_access_token')
    localStorage.removeItem('smb_refresh_token')
    localStorage.removeItem('smb_last_created_game')
    localStorage.removeItem('smb_user_type')
    localStorage.removeItem('vcc_admin_access_token')
    toast.success(i18n.t('logout.success'), { autoClose: 3000 })
  },
  [signedUp.type]: () => {
    toast.success(i18n.t('success.confirmation'), {
      autoClose: false
    })
  },
  [logInError.type]: (state, action) => {
    toast.error(action.payload.message)
  },
  [signUpError.type]: (state, action) => {
    toast.error(action.payload.message)
  },
  [gameCreated.type]: (state, action) => {
    state.lastCreatedGame = action.payload
    state.gameInfo = null
    state.lobbyTeams = null
    localStorage.setItem('smb_last_created_game', JSON.stringify(action.payload))
    toast.success(i18n.t('game_selection.game_created'), { autoClose: 3000 })
  },
  [gameCreateError.type]: (state, action) => {
    toast.error(action.payload.message || i18n.t('game_selection.create_error'))
  },
  [lastCreatedGameCleared.type]: (state) => {
    state.lastCreatedGame = null
    state.gameInfo = null
    state.lobbyTeams = null
    localStorage.removeItem('smb_last_created_game')
  },
  [lastCreatedGameMetadataCleared.type]: (state) => {
    state.lastCreatedGame = null
    localStorage.removeItem('smb_last_created_game')
  },
  [gameJoined.type]: (state, action) => {
    state.lastCreatedGame = action.payload
    if (!action.payload?.pending_team_choice) {
      state.gameInfo = null
      state.lobbyTeams = null
    }
    if (action.payload) {
      localStorage.setItem('smb_last_created_game', JSON.stringify(action.payload))
    }
    toast.success(i18n.t('game_selection.game_joined'), { autoClose: 3000 })
  },
  [gameJoinError.type]: (state, action) => {
    toast.error(action.payload.message || i18n.t('game_selection.join_error'))
  },
  [gameStarted.type]: () => {
    toast.success(i18n.t('lobby.game_started'), { autoClose: 3000 })
  },
  [gameStartError.type]: (state, action) => {
    toast.error(action.payload.message || i18n.t('lobby.start_error'))
  },
  [gameInfoFetched.type]: (state, action) => {
    state.gameInfo = action.payload
    if (action.payload?.teams) state.lobbyTeams = action.payload.teams
  },
  [lobbyPublicDataCleared.type]: (state) => {
    state.gameInfo = null
    state.lobbyTeams = null
  },
  [lobbyTeamsUpdated.type]: (state, action) => {
    state.lobbyTeams = action.payload
    if (state.gameInfo) state.gameInfo.teams = action.payload
  },
  [teamNameUpdated.type]: (state, action) => {
    const { teamId, name } = action.payload
    const updateTeamName = (teams) => {
      if (!teams) return
      const team = teams.find((t) => t.id === teamId)
      if (team) team.name = name
    }
    updateTeamName(state.lobbyTeams)
    if (state.gameInfo?.teams) updateTeamName(state.gameInfo.teams)
  }
})

export default authReducer

// PUBLIC ACTIONS
export const signInPlayer = (accessCode) =>
  apiCallBegan({
    url: '/users/sign_in',
    method: 'post',
    data: accessCode,
    onSuccess: loggedIn.type,
    onError: logInError.type
  })

// Sign in to API using game join code + email (finds player in that game by email)
export const signInToGame = (joinCode, email) =>
  apiCallBegan({
    url: '/users/sign_in',
    method: 'post',
    data: { join_code: joinCode, email },
    onSuccess: loggedIn.type,
    onError: logInError.type
  })

// Fetch User's active (started) game from playlearning-admin for re-login redirect
export const fetchActiveGame = (email) => async () => {
  try {
    const response = await axios.get(`${API.ADMIN_API_URL}/api/v1/auth/games/active`, {
      params: { ...API.DEFAULT_PARAMS, email }
    })
    return response.data
  } catch {
    return { game: null }
  }
}

// Resume active game: fetch from admin, sign in as Player if found, return whether resumed
export const resumeActiveGame = (email) => async (dispatch) => {
  const data = await dispatch(fetchActiveGame(email))
  if (!data?.join_code) return { resumed: false }

  try {
    const signInResponse = await axios.post(
      `${API.BASE_URL}/users/sign_in`,
      { join_code: data.join_code, email },
      { params: API.DEFAULT_PARAMS }
    )
    const tokens = signInResponse.data
    if (tokens.access_token) {
      localStorage.setItem('smb_access_token', tokens.access_token)
      if (tokens.refresh_token) {
        localStorage.setItem('smb_refresh_token', tokens.refresh_token)
      }
    }
    dispatch(loggedIn(tokens))
    return { resumed: true }
  } catch {
    return { resumed: false }
  }
}

export const signOut = () =>
  apiCallBegan({
    url: '/users/sign_out',
    method: 'delete',
    onSuccess: loggedOut.type,
    onError: loggedOut.type
  })

export const exchangeAuth0Token = (auth0AccessToken) => async (dispatch) => {
  try {
    const response = await axios.post(
      `${API.BASE_URL}/users/exchange_token`,
      {},
      {
        params: API.DEFAULT_PARAMS,
        headers: {
          Authorization: `Bearer ${auth0AccessToken}`
        }
      }
    )
    exchangePlaylearningAdminToken(auth0AccessToken).catch(() => {})
    dispatch(loggedIn(response.data))
  } catch (error) {
    const message =
      error.response?.data?.message || error.message || i18n.t('login.errors.auth0_failed')
    dispatch(logInError({ message }))
  }
}

export const fetchGameInfo =
  (expectedJoinCode = null) =>
  async (dispatch) => {
    try {
      const response = await axios.get(`${API.BASE_URL}/user/game`, {
        params: { ...API.DEFAULT_PARAMS, _t: Date.now() },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('smb_access_token')}`,
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache'
        }
      })
      const { data } = response
      if (expectedJoinCode) {
        const expected = String(expectedJoinCode).trim().toUpperCase()
        const actual = data?.join_code?.trim().toUpperCase()
        if (actual && actual !== expected) {
          return null
        }
      }
      dispatch(gameInfoFetched(data))
      return data
    } catch (error) {
      // 403 = token is for User, not Player; lobby will show lastCreatedGame only
      // Silently fail - game info fetch is for polling
      return null
    }
  }

export const createGame = (name, description, email) => async (dispatch) => {
  try {
    const response = await axios.post(
      `${API.ADMIN_API_URL}/api/v1/auth/games`,
      { name, description, email },
      { params: API.DEFAULT_PARAMS }
    )
    const gameData = response.data
    dispatch(gameCreated(gameData))

    // If join_code is missing, the external game creation failed - don't redirect
    if (!gameData.join_code) {
      dispatch(gameCreateError({ message: i18n.t('game_selection.create_error') }))
      return null
    }

    // Sign in to the API so the player is redirected to the game lobby
    const signInResponse = await axios.post(
      `${API.BASE_URL}/users/sign_in`,
      { join_code: gameData.join_code, email },
      { params: API.DEFAULT_PARAMS }
    )
    const tokens = signInResponse.data
    // Set token in localStorage before dispatch so Lobby's first request uses the Player token
    if (tokens.access_token) {
      localStorage.setItem('smb_access_token', tokens.access_token)
      if (tokens.refresh_token) {
        localStorage.setItem('smb_refresh_token', tokens.refresh_token)
      }
    }
    dispatch(loggedIn(tokens))
    await dispatch(fetchGameInfo(gameData.join_code))

    return gameData
  } catch (error) {
    const message =
      error.response?.data?.error || error.message || i18n.t('game_selection.create_error')
    dispatch(gameCreateError({ message }))
    return null
  }
}

export const resetGameSession = () => (dispatch) => {
  dispatch(lastCreatedGameCleared())
  dispatch(cleanUserGame())
  dispatch(cleanUserTeam())
  dispatch(cleanUserBank())
}

export const joinGame = (joinCode, email) => async (dispatch, getState) => {
  const normalized = joinCode?.trim().toUpperCase()
  dispatch(cleanUserGame())
  dispatch(cleanUserTeam())
  dispatch(cleanUserBank())
  const storedCode = getState().auth.lastCreatedGame?.join_code?.trim().toUpperCase()
  if (storedCode && storedCode !== normalized) {
    dispatch(lastCreatedGameMetadataCleared())
  }
  try {
    const response = await axios.post(
      `${API.ADMIN_API_URL}/api/v1/auth/games/join`,
      { join_code: joinCode, email },
      { params: API.DEFAULT_PARAMS }
    )
    const gameData = response.data
    dispatch(gameJoined(gameData))

    if (!gameData.join_code) {
      dispatch(gameJoinError({ message: i18n.t('game_selection.join_error') }))
      return null
    }

    // If pending_team_choice, do not sign in yet; user will pick a team in the lobby
    if (gameData.pending_team_choice) {
      return gameData
    }

    // Existing player: sign in to the API
    const signInResponse = await axios.post(
      `${API.BASE_URL}/users/sign_in`,
      { join_code: gameData.join_code, email },
      { params: API.DEFAULT_PARAMS }
    )
    const tokens = signInResponse.data
    if (tokens.access_token) {
      localStorage.setItem('smb_access_token', tokens.access_token)
      if (tokens.refresh_token) {
        localStorage.setItem('smb_refresh_token', tokens.refresh_token)
      }
    }
    dispatch(loggedIn(tokens))
    await dispatch(fetchGameInfo(gameData.join_code))

    return gameData
  } catch (error) {
    const data = error.response?.data
    const message =
      (data && (data.error || data.message)) || error.message || i18n.t('game_selection.join_error')
    dispatch(gameJoinError({ message }))
    return null
  }
}

export const fetchLobbyByJoinCode = (joinCode) => async (dispatch, getState) => {
  const normalized = joinCode?.trim().toUpperCase()
  const existing = getState().auth.gameInfo?.join_code?.trim().toUpperCase()
  if (existing && existing !== normalized) {
    dispatch(lobbyPublicDataCleared())
  }
  try {
    const response = await axios.get(`${API.BASE_URL}/games/lobby`, {
      params: { ...API.DEFAULT_PARAMS, join_code: joinCode }
    })
    dispatch(gameInfoFetched(response.data))
    return response.data
  } catch (error) {
    return null
  }
}

/** After admin join/create: lobby for waiting games; for started games, facilitators may go straight to /game if already a player in that game. */
export const resolvePostJoinRedirectPath = (gameData, userType, email) => async (dispatch) => {
  if (!gameData?.join_code) return ROUTES.APP_URL_GAME_SELECTION

  const jc = gameData.join_code
  // `pending_team_choice` means the requested join code should land in that
  // game's lobby, even if the current session still has stale player context.
  if (gameData.pending_team_choice) {
    return getLobbyUrl(jc)
  }

  if (gameData.aasm_state !== 'started') {
    return getLobbyUrl(jc)
  }

  if (userType === USER_TYPES.facilitator) {
    if (!email) return getLobbyUrl(jc)
    const lobby = await dispatch(fetchLobbyByJoinCode(jc))
    const me = lobby?.facilitators?.find(
      (f) => f.email?.trim().toLowerCase() === email.trim().toLowerCase()
    )
    if (me?.is_player || me?.player_team_id) {
      return ROUTES.APP_URL_HOME
    }
    return getLobbyUrl(jc)
  }

  return ROUTES.APP_URL_HOME
}

export const joinTeam = (gameId, joinCode, email, teamSlot) => async (dispatch) => {
  let gameData
  try {
    const response = await axios.post(
      `${API.ADMIN_API_URL}/api/v1/auth/games/join_team`,
      { game_id: gameId, email, team_slot: teamSlot, join_code: joinCode },
      { params: API.DEFAULT_PARAMS }
    )
    gameData = response.data
    if (!gameData.join_code) {
      dispatch(gameJoinError({ message: i18n.t('game_selection.join_error') }))
      return null
    }
  } catch (error) {
    const data = error.response?.data
    const message =
      (data && (data.error || data.message)) || error.message || i18n.t('game_selection.join_error')
    dispatch(gameJoinError({ message }))
    return null
  }

  try {
    const signInResponse = await axios.post(
      `${API.BASE_URL}/users/sign_in`,
      { join_code: gameData.join_code, email },
      { params: API.DEFAULT_PARAMS }
    )
    const tokens = signInResponse.data
    if (tokens.access_token) {
      localStorage.setItem('smb_access_token', tokens.access_token)
      if (tokens.refresh_token) {
        localStorage.setItem('smb_refresh_token', tokens.refresh_token)
      }
    }
    dispatch(loggedIn(tokens))
    dispatch(
      gameJoined({
        ...gameData,
        pending_team_choice: false
      })
    )
    await dispatch(fetchGameInfo(gameData.join_code))
    return gameData
  } catch {
    dispatch(
      gameJoined({
        ...gameData,
        pending_team_choice: false
      })
    )
    toast.success(
      `${i18n.t('game_selection.game_joined')} ${i18n.t('lobby.joined_refresh_if_needed')}`
    )
    return gameData
  }
}

export const addAiTeam = (gameId, email, teamSlot, decisionStrategyName) => async (dispatch) => {
  try {
    const body = { game_id: gameId, email, team_slot: teamSlot }
    if (decisionStrategyName) {
      body.decision_strategy_name = decisionStrategyName
    }
    await axios.post(`${API.ADMIN_API_URL}/api/v1/auth/games/add_ai_team`, body, {
      params: API.DEFAULT_PARAMS
    })
    await dispatch(fetchGameInfo())
    return true
  } catch (error) {
    const message = error.response?.data?.error || error.message || i18n.t('lobby.add_ai_error')
    toast.error(message)
    return false
  }
}

export const removeAiTeam =
  (adminGameId, email, externalTeamId, fallbackGameApiId) => async (dispatch) => {
    try {
      if (adminGameId && email && externalTeamId) {
        await axios.post(
          `${API.ADMIN_API_URL}/api/v1/auth/games/remove_ai_team`,
          { game_id: adminGameId, email, team_id: externalTeamId },
          { params: API.DEFAULT_PARAMS }
        )
      } else if (fallbackGameApiId && externalTeamId) {
        await axios.delete(`${API.BASE_URL}/games/${fallbackGameApiId}/remove_ai_team`, {
          params: { ...API.DEFAULT_PARAMS, team_id: externalTeamId }
        })
      } else {
        return false
      }
      await dispatch(fetchGameInfo())
      return true
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        i18n.t('lobby.remove_ai_error')
      toast.error(message)
      return false
    }
  }

export const startGame = (gameId, email) => async (dispatch) => {
  try {
    await axios.post(
      `${API.ADMIN_API_URL}/api/v1/auth/games/start`,
      { game_id: gameId, email },
      { params: API.DEFAULT_PARAMS }
    )
    dispatch(gameStarted())
    dispatch(fetchGameInfo())
    return true
  } catch (error) {
    const data = error.response?.data
    const message =
      (data && (data.error || data.message)) || error.message || i18n.t('lobby.start_error')
    dispatch(gameStartError({ message }))
    return false
  }
}

export const skipTutorialYear = (gameId) => async (dispatch) => {
  try {
    const response = await axios.post(
      `${API.BASE_URL}/games/${gameId}/error_management/skip_tutorial_year`,
      {},
      {
        params: API.DEFAULT_PARAMS,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('smb_access_token')}`
        }
      }
    )
    const payload = response.data
    if (payload?.status === 'success') {
      dispatch(fetchGameInfo())
      return true
    }
    const message = payload?.message || i18n.t('lobby.skip_tutorial_error')
    toast.error(message)
    return false
  } catch (error) {
    const data = error.response?.data
    const message =
      (data && (data.message || data.error)) || error.message || i18n.t('lobby.skip_tutorial_error')
    toast.error(message)
    return false
  }
}

export const setLobbyReady = (ready) => async (dispatch) => {
  const response = await axios.put(
    `${API.BASE_URL}/user/ready`,
    { ready },
    {
      params: API.DEFAULT_PARAMS,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('smb_access_token')}`
      }
    }
  )
  dispatch(gameInfoFetched(response.data))
  return response.data
}

const doUpdatePlayerRequest = (payload) =>
  axios.patch(`${API.BASE_URL}/user/player`, payload, {
    params: API.DEFAULT_PARAMS,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('smb_access_token')}`
    }
  })

export const updatePlayer = (payload) => async (dispatch) => {
  try {
    const response = await doUpdatePlayerRequest(payload)
    await dispatch(fetchGameInfo())
    return response.data
  } catch (error) {
    if (error.response?.status === 401) {
      try {
        await tokenRefresh(dispatch, () => {})
        const retryResponse = await doUpdatePlayerRequest(payload)
        await dispatch(fetchGameInfo())
        return retryResponse.data
      } catch (refreshError) {
        dispatch(loggedOut())
        throw refreshError
      }
    }
    throw error
  }
}

export const updateGame = (payload) => async (dispatch, getState) => {
  await axios.patch(`${API.BASE_URL}/user/game`, payload, {
    params: API.DEFAULT_PARAMS,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('smb_access_token')}`
    }
  })
  const adminGameId = getState().auth.lastCreatedGame?.game_id
  if (adminGameId) {
    try {
      await axios.patch(`${API.ADMIN_API_URL}/api/v1/auth/games/${adminGameId}`, payload, {
        params: API.DEFAULT_PARAMS
      })
    } catch {
      toast.warn(i18n.t('lobby.admin_sync_warning'))
    }
  }
  await dispatch(fetchGameInfo())
  return getState().auth.gameInfo
}

export const updateTeam = (payload) => async (dispatch) => {
  const response = await axios.patch(`${API.BASE_URL}/user/team`, payload, {
    params: API.DEFAULT_PARAMS,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('smb_access_token')}`
    }
  })
  // Optimistically update team name in lobbyTeams to avoid flicker
  // from stale poll responses arriving after this update.
  const updatedTeam = response.data
  if (updatedTeam?.id && payload.name) {
    dispatch(teamNameUpdated({ teamId: updatedTeam.id, name: payload.name }))
  }
  await dispatch(fetchGameInfo())
  return response.data
}

export const clearLastCreatedGame = () => (dispatch) => {
  dispatch(lastCreatedGameCleared())
}

export const surrenderGame = () => async (dispatch, getState) => {
  const gameId = getState().user?.game?.data?.id
  if (!gameId) return
  try {
    await axios.post(
      `${API.BASE_URL}/games/${gameId}/surrender`,
      {},
      {
        params: API.DEFAULT_PARAMS,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('smb_access_token')}`
        }
      }
    )
    await syncSurrenderState(dispatch, getState)
  } catch {
    toast.error(i18n.t('game_selection.join_error'))
  }
}

export const updateLobbyTeams = (teams) => (dispatch) => {
  dispatch(lobbyTeamsUpdated(teams))
}

export const facilitatorEnterGame = (teamId) => async (dispatch, getState) => {
  const expectedJoinCode =
    getState().auth.gameInfo?.join_code || getState().auth.lastCreatedGame?.join_code || null
  await axios.put(
    `${API.BASE_URL}/user/update_current_team`,
    { team_id: teamId },
    {
      params: API.DEFAULT_PARAMS,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('smb_access_token')}`
      }
    }
  )
  await dispatch(fetchGameInfo(expectedJoinCode))
}

export const facilitatorJoinLobby = (joinCode) => async (dispatch) => {
  try {
    await axios.post(
      `${API.BASE_URL}/user/facilitator_join_team`,
      { join_code: joinCode },
      {
        params: API.DEFAULT_PARAMS,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('smb_access_token')}`
        }
      }
    )
    await dispatch(fetchLobbyByJoinCode(joinCode))
  } catch {
    // Silently fail — facilitator just won't appear in box until they take action
  }
}

export const facilitatorJoinTeamAsPlayer =
  (teamId, joinCode, teamSlot) => async (dispatch, getState) => {
    const body = teamId ? { team_id: teamId } : { join_code: joinCode, team_slot: teamSlot }
    await axios.post(`${API.BASE_URL}/user/facilitator_join_team`, body, {
      params: API.DEFAULT_PARAMS,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('smb_access_token')}`
      }
    })
    await syncAdminLobbyAfterSeatChange(getState)
    if (joinCode) {
      await dispatch(fetchLobbyByJoinCode(joinCode))
      await dispatch(fetchGameInfo(joinCode))
    }
  }

export const facilitatorLeaveTeam = (joinCode) => async (dispatch, getState) => {
  await axios.delete(`${API.BASE_URL}/user/facilitator_leave_team`, {
    params: API.DEFAULT_PARAMS,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('smb_access_token')}`
    }
  })
  await syncAdminLobbyAfterSeatChange(getState)
  if (joinCode) {
    await dispatch(fetchLobbyByJoinCode(joinCode))
    await dispatch(fetchGameInfo(joinCode))
  }
}

export const switchTeam =
  (teamId, teamSlot = null) =>
  async (dispatch, getState) => {
    const body = teamId != null && teamId !== '' ? { team_id: teamId } : { team_slot: teamSlot }
    await axios.put(`${API.BASE_URL}/user/switch_team`, body, {
      params: API.DEFAULT_PARAMS,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('smb_access_token')}`
      }
    })

    await syncAdminLobbyAfterSeatChange(getState)

    const expectedJoinCode =
      getState().auth.gameInfo?.join_code || getState().auth.lastCreatedGame?.join_code || null
    await dispatch(fetchGameInfo(expectedJoinCode))
  }
